import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CONSTANT } from '@const';
import { LegalEntityContext, ScreenSizeContext } from '@context';
import { AuthorisationService, WaterAccountService } from '@data';
import { Pagination, Skeleton, Tabs, TextField } from '@general';
import { Close, Done, Edit } from '@public';
import { AuthorisationSummary, PageFilter, WAMeterCase, WaterAccountType } from '@types';
import {
	convertDateAndTimeToDisplayFormat,
	getMeterCaseReferenceInfo,
	getReason,
	handlePageChange,
} from '@utils';
import styles from './waterAccountDetails.module.scss';

const MAX_TEXT_NAME_LIMIT = 100;

export interface WaterAccountDetailsProps {
	waterAccount: WaterAccountType;
	onNameChange: (newName: string, id: string) => void;
}

export const WaterAccountDetails = ({ waterAccount, onNameChange }: WaterAccountDetailsProps) => {
	const {
		wateraccountId: waterAccountId,
		accountName,
		accountReference,
		managementGroup,
		managementSubGroup,
		devices,
		works,
		meterCases,
	} = waterAccount;
	const fullAuthorisationsTabName = 'Authorisations';
	const [authorisationsTabName, setAuthorisationsTabName] = useState(fullAuthorisationsTabName);
	const [DETAILS, AUTHORISATIONS, DEVICES, WORKS, METERCASES] = [
		'Details',
		authorisationsTabName,
		'Devices',
		'Works',
		'Meter Cases',
	];
	// Context
	const { legalEntity } = useContext(LegalEntityContext);
	const { isMobile, isTablet, isSmallDT, isMediumDT } = useContext(ScreenSizeContext);
	// State
	const [selectedTab, setSelectedTab] = useState(DETAILS);
	const [authorisationsPageFilter, setAuthorisationsPageFilter] = useState<PageFilter>(
		new PageFilter(AuthorisationService.itemsPerPage),
	);
	const [authorisations, setAuthorisations] = useState<AuthorisationSummary[]>([]);
	const [showPaginator, setShowPaginator] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [editStatus, setEditStatus] = useState<'valid' | 'error'>('valid');
	const [curName, setCurName] = useState(accountName);
	const [newName, setNewName] = useState(accountName);
	// Memo
	const hasAuthorisationsTab = useMemo(() => authorisations?.length >= 6, [authorisations?.length]);
	const hasDevicesTab = useMemo(() => devices.length >= 6, [devices.length]);
	const hasWorksTab = useMemo(() => works.length >= 6, [works.length]);
	const hasMCTab = useMemo(() => meterCases && meterCases.length >= 1, [meterCases]);
	const availableTabs: string[] = useMemo(() => {
		const tabs = [DETAILS];
		if (hasAuthorisationsTab) {
			tabs.push(AUTHORISATIONS);
		}
		if (hasDevicesTab) {
			tabs.push(DEVICES);
		}
		if (hasMCTab) {
			tabs.push(METERCASES);
		}
		if (hasWorksTab) {
			tabs.push(WORKS);
		}
		return tabs;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasAuthorisationsTab, hasDevicesTab, hasWorksTab, hasMCTab, authorisationsTabName]);

	// Data
	const getAuthorisations = useQuery({
		queryKey: [CONSTANT.AUTHORISATIONS_API_URL, legalEntity?.id, authorisationsPageFilter.offSet, waterAccountId], // prettier-ignore
		queryFn: () =>
			legalEntity ? AuthorisationService.getAuthorisations(legalEntity.id, authorisationsPageFilter, { waterAccountId: waterAccountId }) : undefined, // prettier-ignore
		enabled: !!legalEntity?.id && !!waterAccountId && authorisationsPageFilter.offSet != undefined, // prettier-ignore
		placeholderData: keepPreviousData,
	});

	useEffect(() => {
		if (getAuthorisations.data) {
			const isFirstPageData = authorisationsPageFilter.offSet === 0;
			if (isFirstPageData) {
				setAuthorisations(getAuthorisations.data?.items ?? []);
			}
		}
	}, [getAuthorisations.data, authorisationsPageFilter.offSet]);

	// Life Cycle
	useEffect(() => {
		setCurName(accountName);
		setNewName(accountName);
	}, [accountName]);

	useEffect(() => {
		resetEdit();
	}, [waterAccountId]);

	/**
	 * Adapt the name of the Authorisations tab based on the number of available tabs
	 * and the current screen size. If there are 4 or more tabs or there are exactly 3 tabs and the screen
	 * is either small desktop or mobile, the name is shortened to 'Auths.'
	 * Otherwise it remains as 'Authorisations'
	 */
	useEffect(() => {
		const hasToAdaptTabName = availableTabs.length >= 4;
		const hasToAdaptTabNameBySpecialCase =
			availableTabs.length == 3 && ((isSmallDT && !isTablet) || isMobile);
		let nextAuthorisationsTabName = fullAuthorisationsTabName;
		if (hasToAdaptTabName || hasToAdaptTabNameBySpecialCase) {
			nextAuthorisationsTabName = 'Auths.';
		}
		setAuthorisationsTabName(nextAuthorisationsTabName);
	}, [isMobile, isTablet, isSmallDT, isMediumDT, availableTabs]);

	// On selected water account
	useEffect(() => {
		setShowPaginator(false); // re-render Pagination component
		setAuthorisationsPageFilter({
			...authorisationsPageFilter,
			offSet: PageFilter.initialOffSet,
		}); // reset pageFilter
		setAuthorisations([]);
	}, [waterAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

	// Reset selected tab on changed water account
	useEffect(() => {
		setSelectedTab(DETAILS);
	}, [DETAILS, waterAccountId]);

	// On first data fetched for selected water account, finish re-render Pagination component
	useEffect(() => {
		if (getAuthorisations.isFetchedAfterMount) {
			setShowPaginator(true); // re-render Pagination
		}
	}, [getAuthorisations.isFetchedAfterMount]);

	// Helpers
	const details = [
		{
			header: 'Management group',
			content: managementGroup,
			show: true,
		},
		{
			header: 'Management subgroup',
			content: managementSubGroup,
			show: true,
		},
		{
			header: fullAuthorisationsTabName,
			content: hasAuthorisationsTab
				? `Please select the '${AUTHORISATIONS}' tab to view`
				: authorisations.map(auth => {
						return `${auth.reference} (${auth.authorisationType})`;
					}),
			show: true,
			isLoading: getAuthorisations.isFetching,
		},
		{
			header: 'Devices',
			content: hasDevicesTab
				? `Please select the '${DEVICES}' tab to view`
				: devices.map(device => {
						return `${device.reference} (${device.make} - ${device.model}: ${device.deviceType})`;
					}),
			show: true,
		},
		{
			header: 'Works',
			content: hasWorksTab
				? `Please select the '${WORKS}' tab to view`
				: works.map(work => {
						return `${work.reference} (${work.type})`;
					}),
			show: true,
		},
	];

	const devicesContent = [
		{
			header: 'Devices',
			content: devices.map(device => {
				return `${device.reference} (${device.make} - ${device.model}: ${device.deviceType})`;
			}),
			show: true,
		},
	];

	const worksContent = [
		{
			header: 'Works',
			content: works.map(work => {
				return `${work.reference} (${work.type})`;
			}),
			show: true,
		},
	];

	const resetEdit = () => {
		setEditMode(false);
		setEditStatus('valid');
	};

	const onEdit = async () => {
		if (curName !== newName) {
			const res = await WaterAccountService.updateWaterAccount(
				legalEntity?.id ? legalEntity?.id : '',
				waterAccount.wateraccountId,
				newName,
			);
			if (res) {
				setCurName(newName);
				onNameChange(newName, waterAccountId);
			}
			resetEdit();
		} else {
			resetEdit();
		}
	};

	const onPageChange = (selectedPage: number) =>
		handlePageChange(selectedPage, getAuthorisations, setAuthorisationsPageFilter);

	const SwitchSelectedTabSection = () => {
		switch (selectedTab) {
			case DETAILS:
				return createSection(details);
			case AUTHORISATIONS:
				return createAuthorisationsSection();
			case DEVICES:
				return createSection(devicesContent);
			case METERCASES:
				return createMCSection('Meter Case', meterCases);
			case WORKS:
				return createSection(worksContent);
			default:
				return null;
		}
	};

	const createSection = (
		section: Array<{
			header: string;
			content: string | string[] | null;
			show: boolean;
			isLoading?: boolean;
		}>,
	) => {
		return (
			<div className={styles[`detail-container`]}>
				<div>
					{section.map(({ header, content, show, isLoading }, id) => {
						return (
							<Fragment key={`section-${id}`}>
								{show && (
									<>
										{Array.isArray(content) ? (
											<div className={[styles.detail, styles['detail-vertical']].join(' ')}>
												<p className={styles['detail-header']}>{header}</p>
												{isLoading ? (
													<Skeleton customStyles={styles['skeleton-details']} />
												) : content.length > 0 ? (
													<ul className={styles[`detail-list`]}>
														{content.map((item, key) => (
															<li className={styles[`detail-list-item`]} key={`content-${key}`}>
																<p>{item}</p>
															</li>
														))}
													</ul>
												) : (
													<p className={styles[`detail-list-item`]}>N/A</p>
												)}
											</div>
										) : (
											<div className={styles.detail}>
												<p className={styles['detail-header']}>{header}</p>
												{isLoading ? (
													<Skeleton customStyles={styles['skeleton-details']} />
												) : (
													<p className={styles['detail-content']}>{content ? content : ' - '}</p>
												)}
											</div>
										)}
									</>
								)}
							</Fragment>
						);
					})}
				</div>
			</div>
		);
	};

	const SublistSkeleton = () => (
		<>
			{Array.from(Array(8)).map((_el, id) => {
				return (
					<tr key={`skeletion-${id}`}>
						<td>
							<Skeleton customStyles={styles['skeleton-sublist']} />
						</td>
					</tr>
				);
			})}
		</>
	);

	const createAuthorisationsSection = () => {
		return (
			<>
				<div className={styles.sublist}>
					{authorisationsTabName != fullAuthorisationsTabName && (
						<p className={styles['detail-header']}>Authorisations</p>
					)}
					<table>
						<tbody>
							{getAuthorisations.isLoading || getAuthorisations.isFetching ? (
								<SublistSkeleton />
							) : (
								getAuthorisations.data?.items?.map((item, id) => {
									return (
										<tr key={`authorisation-${id}`} className={styles['sublist-row']}>
											<td>
												{item.reference} ({item.authorisationType})
											</td>
										</tr>
									);
								})
							)}
						</tbody>
					</table>
				</div>
				{showPaginator &&
					getAuthorisations.data &&
					getAuthorisations.data.items.length > 0 && (
						<Pagination
							itemCounts={getAuthorisations.data.metadata.totalCount}
							paginationLimit={getAuthorisations.data.metadata.limit}
							handleClick={onPageChange}
						/>
					)}
			</>
		);
	};

	const createMCSection = (header: string, content: WAMeterCase[]) => {
		return (
			<div className={styles[`detail-container`]}>
				<div className={[styles.detail, styles['detail-vertical']].join(' ')}>
					{content.map((item, key) => (
						<div
							className={[styles.detail, styles['detail-no-gap']].join(' ')}
							key={`content-${key}`}
						>
							<p className={styles['detail-header']}>Meter case code:</p>
							<p className={styles[`detail-content`]}>{item.meterCaseCode}</p>
							<p className={styles['detail-header']}>Reason:</p>
							<p className={styles[`detail-content`]}>{getReason(item)}</p>
							{(() => {
								const referenceInfo = getMeterCaseReferenceInfo(item);
								return (
									<>
										<p className={styles['detail-header']}>{referenceInfo.label}:</p>
										<p className={styles[`detail-content`]}>{referenceInfo.value}</p>
									</>
								);
							})()}
							<p className={styles['detail-header']}>Response due: </p>
							<p className={styles[`detail-content`]}>
								{item.responseDueDate
									? convertDateAndTimeToDisplayFormat(item.responseDueDate)
									: null}
							</p>
						</div>
					))}
				</div>
			</div>
		);
	};

	return (
		<div className={styles.container} data-testid="waterAccountDetails">
			<div className={styles.title}>
				<h2>Water account details</h2>
			</div>
			<div className={styles['border-title']} />
			{!editMode ? (
				<div
					className={styles.name}
					onClick={() => {
						setEditMode(true);
					}}
				>
					<div className={styles['name-text']}>
						{curName ? (
							<h3 data-testid="waterAccountName">{curName}</h3>
						) : (
							<h3 className={styles['name-text-placeholder']} data-testid="waterAccountName">
								Edit water account name
							</h3>
						)}
					</div>
					<Edit customStyles={styles['name-icon']} />
				</div>
			) : (
				<div className={styles.edit}>
					<TextField
						customStyles={styles['edit-text']}
						placeholder={'Please provide a name'}
						errorMsg="Please input a new name or press x to cancel"
						status={editStatus}
						handleChange={e => {
							setNewName(e.target.value);
						}}
						maxLength={MAX_TEXT_NAME_LIMIT}
						defaultValue={curName}
						testId="waterAccountName"
					/>
					<div data-testid="editNameIcons">
						<Close
							customStyles={styles['edit-icon']}
							handleClick={() => {
								resetEdit();
							}}
						/>
						<Done customStyles={styles['edit-icon']} handleClick={onEdit} />
					</div>
				</div>
			)}
			<div className={styles.name}>
				<div className={styles['name-text']} data-testid="waterAccountReference">
					Water account reference: {accountReference}
				</div>
			</div>
			<div className={styles.tabs}>
				<Tabs
					customStyles={styles['tabs-large']}
					tabs={availableTabs}
					handleClick={name => setSelectedTab(name)}
					selectedTab={selectedTab}
				/>
				<div className={styles['border-tab']} />
			</div>
			<div className={styles['sublist-container']}>{SwitchSelectedTabSection()}</div>
		</div>
	);
};
